import { action, computed, observable, reaction, runInAction } from "mobx";
import {
	IEventPlayers,
	IEventStatsBase,
	MatchStatAvailabilityIndex,
	RequestType,
	StatusCodes,
} from "@data-types/sport-stats/SportStatsTypes";
import { LoaderStore } from "@state/stores/common";
import { SportsStatsApiService } from "@api-services/sport-stats";
import LivePlayerStatisticsStore from "./LivePlayerStatisticsStore";

export default class TeamLineupsStore {
	playerStatisticsStore: LivePlayerStatisticsStore;
	loader: LoaderStore = new LoaderStore();

	@observable isTeamLineupsActive = false;
	@observable isFetchingData = false;
	@observable teamLineups: IEventPlayers | null = null;
	@observable isError = false;

	@computed get isTeamLineupsAvailable() {
		return !!this.teamLineups;
	}

	@computed get isLiveOrHistory(): boolean {
		return (
			this.playerStatisticsStore.eventStore.event?.isLive ||
			this.playerStatisticsStore.eventStore.isOfferHistory
		);
	}

	constructor(playerStatisticsStore: LivePlayerStatisticsStore) {
		this.playerStatisticsStore = playerStatisticsStore;

		reaction(
			() => [
				this.isTeamLineupsActive,
				this.playerStatisticsStore.isPlayerStatActive,
			],
			([isLineupsActive, isPlayerStatsActive]) => {
				if (!this.playerStatisticsStore.eventStore.event?.liveStatus) {
					return;
				}
				if (isLineupsActive || isPlayerStatsActive) {
					this.playerStatisticsStore.disposeAvailabilitySubscription();
				} else {
					this.playerStatisticsStore.subscribeToAvailability();
				}
			}
		);

		reaction(
			() => this.playerStatisticsStore.playerStatisticsRawData,
			(playerData) => {
				runInAction(() => {
					this.teamLineups = playerData;
				});
			}
		);
	}

	@action.bound
	async checkPrematchTeamLineupsAvailability() {
		if (!WEBPACK_IS_SPORT_STATISTICS_ENABLED) {
			return;
		}

		if (this.playerStatisticsStore.eventStore.event?.liveStatus) {
			return;
		}

		if (this.playerStatisticsStore.eventStore.sport?.abrv != "soccer") {
			return;
		}

		if (!this.isTeamLineupsAvailable) {
			const response = await this.fetchTeamLineupsAvailability();

			if (
				response &&
				response.statusCode !== StatusCodes.NOT_AVAILABLE &&
				response?.response?.[0]?.data?.eventPlayers ==
					MatchStatAvailabilityIndex.AVAILABLE
			) {
				await this.fetchTeamLineups();
			} else {
				this.teamLineups = null;
				this.isError = true;
			}
		}
	}

	@action.bound
	async fetchTeamLineupsAvailability() {
		const filter = this.createTeamLineupsFilter(RequestType.Availability);

		if (!filter) {
			return null;
		}

		try {
			return await SportsStatsApiService.fetchPlayerLiveStatistics({
				specific: filter,
			});
		} catch (error) {
			return null;
		}
	}

	@action.bound
	toggleTeamLineups() {
		this.isTeamLineupsActive = !this.isTeamLineupsActive;
	}

	@action.bound
	async fetchTeamLineups() {
		this.isFetchingData = true;

		const filter = this.createTeamLineupsFilter(RequestType.Data);

		if (!filter) {
			this.isFetchingData = false;
			return;
		}

		this.loader.suspend();

		try {
			const teamLineupsResponse =
				await SportsStatsApiService.fetchPlayerLiveStatistics({
					specific: filter,
				});

			if (teamLineupsResponse.statusCode == StatusCodes.NOT_AVAILABLE) {
				throw new Error("Team Lineups is not available.");
			}

			if (
				!teamLineupsResponse?.response?.length ||
				!teamLineupsResponse?.response?.[0].data
			) {
				throw new Error("Team Lineups is not available.");
			}

			runInAction(() => {
				if (teamLineupsResponse.response != null) {
					const response = teamLineupsResponse.response[0];

					if (response.data.eventPlayers != null) {
						this.teamLineups = response.data.eventPlayers;
					}
					this.isError = false;
				}
			});
		} catch (error) {
			this.isError = true;
			this.teamLineups = null;
		} finally {
			this.loader.resume();
			this.isFetchingData = false;
		}
	}

	createTeamLineupsFilter<T extends RequestType>(
		requestType: T
	): { RequestType: T; Events: IEventStatsBase[] } | null {
		const filter =
			this.playerStatisticsStore.generatePlayerStatsEventFilter();
		if (!filter) {
			return null;
		}

		return {
			RequestType: requestType,
			Events: [{ ...filter, Lineups: true }],
		};
	}

	colorGradient = (rating: number) => {
		if (!rating || rating === 0.0) {
			return "none";
		} else if (rating <= 6) {
			return "bottom";
		} else if (rating <= 6.5) {
			return "low";
		} else if (rating <= 7) {
			return "medium";
		} else if (rating <= 8) {
			return "good";
		} else if (rating <= 9) {
			return "high";
		} else if (rating <= 10) {
			return "top";
		}
		return "none";
	};

	createTeamLineupsPositions = (
		formation: string,
		playerCount: number,
		isHome: boolean,
		fieldDimensions: { fieldWidth: number; fieldHeight: number },
		spacing: { [key: string]: number },
		layout: "vertical" | "horizontal" = "vertical"
	) => {
		const lines = formation.split("-").map(Number);
		const positions: { x: number; y: number }[] = [];
		let playerIndex = 1;
		if (layout === "vertical") {
			const teamHalf =
				(fieldDimensions.fieldHeight / 2 - 90) / (lines.length + 0.5);
			const teamStartLine = isHome
				? 50
				: fieldDimensions.fieldHeight - 50;
			positions.push({
				x: fieldDimensions.fieldWidth / 2,
				y: teamStartLine,
			});
			lines.forEach((linePlayerCount, lineIndex) => {
				const formationLine = isHome
					? teamStartLine + teamHalf * (lineIndex + 1.2)
					: teamStartLine - teamHalf * (lineIndex + 1.2);

				const sideSpacing =
					spacing[String(linePlayerCount)] || spacing.default;

				const fieldWidth = fieldDimensions.fieldWidth - 2 * sideSpacing;

				for (
					let i = 0;
					i < linePlayerCount && playerIndex < playerCount;
					i++, playerIndex++
				) {
					let playerPositionX;
					if (linePlayerCount === 1) {
						playerPositionX = fieldDimensions.fieldWidth / 2;
					} else {
						const idx = isHome ? i : linePlayerCount - 1 - i;
						playerPositionX =
							sideSpacing +
							fieldWidth * (idx / (linePlayerCount - 1));
					}
					positions.push({ x: playerPositionX, y: formationLine });
				}
			});
		} else {
			const teamStartLine = isHome ? 50 : fieldDimensions.fieldWidth - 50;
			const sectionWidth =
				(fieldDimensions.fieldWidth / 2 - 90) / (lines.length + 0.5);
			positions.push({
				x: teamStartLine,
				y: fieldDimensions.fieldHeight / 2,
			});
			lines.forEach((linePlayerCount, lineIndex) => {
				const formationLine = isHome
					? teamStartLine + sectionWidth * (lineIndex + 1.2)
					: teamStartLine - sectionWidth * (lineIndex + 1.2);

				const sideSpacing =
					spacing[String(linePlayerCount)] ?? spacing.default;

				const fieldWidth =
					fieldDimensions.fieldHeight - 2 * sideSpacing;
				for (
					let i = 0;
					i < linePlayerCount && playerIndex < playerCount;
					i++, playerIndex++
				) {
					let playerPositionY;
					if (linePlayerCount === 1) {
						playerPositionY = fieldDimensions.fieldHeight / 2;
					} else {
						const idx = isHome ? linePlayerCount - 1 - i : i;
						playerPositionY =
							sideSpacing +
							fieldWidth * (idx / (linePlayerCount - 1));
					}
					positions.push({ x: formationLine, y: playerPositionY });
				}
			});
		}
		return positions;
	};

	@action.bound
	onDispose() {
		this.teamLineups = null;
		this.isFetchingData = false;
		this.isError = false;
	}
}
